/**
 * SpecialtyInspectionWizard
 * ----------------------------------------------------------------------------
 * Category-aware pre/post-hire wizard rendered when the booked listing's
 * vehicle_category === 'specialty_asset'. Reuses the existing evidence storage,
 * S3 upload pipeline, canonical lifecycle endpoints and audit flow. Only the
 * template shape differs from the vehicle default wizard.
 *
 * Backend submission contracts:
 *   POST /api/bookings/{id}/prehire/submit   body: { template_type: 'specialty_asset', specialty_data, exterior_photos, ... }
 *   POST /api/bookings/{id}/posthire/submit  body: { template_type: 'specialty_asset', specialty_data, return_exterior_photos, ... }
 */
import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Camera, CheckCircle2, ChevronLeft, ChevronRight, Loader2, X, UploadCloud } from 'lucide-react';
import { getChecklistTemplateForCategory, TEMPLATE_TYPES, PHASES } from '../lib/inspectionTemplates';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// -----------------------------------------------------------------------------
// Photo upload — reuses the canonical /api/uploads/checklists/upload pipeline
// used by the default vehicle wizard. Same auth/audit/S3/thumbnail behaviour.
// -----------------------------------------------------------------------------
// Map a specialty prompt key to the backend's canonical photo_type taxonomy.
// Backend validates photo_type ∈ {exterior, interior, odometer, fuel, damage, ...}
const mapPhotoType = (promptKey) => {
  if (!promptKey) return 'exterior';
  if (promptKey.includes('exterior')) return 'exterior';
  if (promptKey.includes('damage')) return 'damage';
  // Equipment, interior, tow, power, etc. all group under 'interior' for
  // storage/audit bucketing — specialty-specific context is preserved in the
  // specialty_data payload submitted alongside the checklist.
  return 'interior';
};

const uploadPhoto = async (file, token, bookingId, phase, promptKey) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('booking_id', bookingId);
  formData.append('checklist_type', phase === PHASES.POSTHIRE ? 'posthire' : 'prehire');
  formData.append('photo_type', mapPhotoType(promptKey));
  const res = await axios.post(`${API}/uploads/checklists/upload`, formData, {
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
    timeout: 60000,
  });
  const s3Key = res.data.s3_key || res.data.key || null;
  const thumbKey = res.data.thumbnail_key || s3Key;
  // Fetch short-lived signed URL via the same canonical endpoint used by the
  // vehicle wizard so the thumbnail renders immediately after upload.
  let thumbUrl = null;
  try {
    if (thumbKey) {
      const encoded = encodeURIComponent(thumbKey);
      const signed = await axios.get(`${API}/uploads/checklists/${bookingId}/${encoded}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      thumbUrl = signed.data?.url || null;
    }
  } catch (_e) {
    thumbUrl = null;
  }
  return { s3Key, thumbUrl };
};

const PhotoGrid = ({ items, min, max, onAdd, onRemove, uploading, testIdPrefix }) => (
  <div className="space-y-2">
    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
      {(items || []).map((it, i) => (
        <div key={`${it.s3Key}-${i}`} className="relative aspect-square rounded-lg overflow-hidden bg-slate-100 border border-slate-200">
          {it.thumbUrl ? (
            <img src={it.thumbUrl} alt="" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-slate-400 text-[10px] text-center px-1">{it.s3Key?.slice(-10)}</div>
          )}
          <button
            type="button"
            className="absolute top-1 right-1 w-6 h-6 rounded-full bg-white/95 text-red-600 shadow flex items-center justify-center"
            onClick={() => onRemove(i)}
            data-testid={`${testIdPrefix}-remove-${i}`}
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      ))}
      {(!max || (items || []).length < max) && (
        <label className={`aspect-square rounded-lg border-2 border-dashed flex flex-col items-center justify-center cursor-pointer text-slate-500 hover:bg-slate-50 ${uploading ? 'opacity-60 pointer-events-none' : ''}`}>
          {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <UploadCloud className="w-5 h-5" />}
          <span className="text-[10px] mt-1">Add photo</span>
          <input
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) onAdd(f); e.target.value = ''; }}
            data-testid={`${testIdPrefix}-add`}
          />
        </label>
      )}
    </div>
    <p className="text-xs text-slate-500">
      {items?.length || 0} of {max || '∞'} photos — minimum {min || 0}
    </p>
  </div>
);

const SpecialtyInspectionWizard = ({ booking, phase, onClose, onSubmitted, token }) => {
  const vehicleCategory = booking?.vehicle?.vehicle_category || 'specialty_asset';
  const template = useMemo(
    () => getChecklistTemplateForCategory(vehicleCategory, phase),
    [vehicleCategory, phase]
  );
  const sections = template.sections || [];

  const [stepIdx, setStepIdx] = useState(0);
  const [state, setState] = useState({});
  const [uploadingKey, setUploadingKey] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const getVal = (key) => state[key];
  const setVal = (key, val) => setState((s) => ({ ...s, [key]: val }));

  const handlePhotoAdd = async (key, file) => {
    try {
      setUploadingKey(key);
      const bookingId = booking.booking_id || booking.id;
      const { s3Key, thumbUrl } = await uploadPhoto(file, token, bookingId, phase, key);
      if (!s3Key) throw new Error('upload failed');
      const current = getVal(key) || [];
      setVal(key, [...current, { s3Key, thumbUrl }]);
    } catch (e) {
      toast.error('Photo upload failed — please try again');
    } finally {
      setUploadingKey(null);
    }
  };

  const handlePhotoRemove = (key, idx) => {
    const current = getVal(key) || [];
    setVal(key, current.filter((_, i) => i !== idx));
  };

  const currentSection = sections[stepIdx];

  const validateSection = (section) => {
    for (const p of section.prompts) {
      if (!p.required) continue;
      const v = getVal(p.key);
      if (p.type === 'photos' && (!Array.isArray(v) || v.length < (p.min || 0))) {
        return `${p.label}: minimum ${p.min || 0} photo(s) required`;
      }
      if (p.type === 'checkbox' && v !== true) {
        return `${p.label}: please tick to acknowledge`;
      }
      if (p.type === 'text' && (!v || !String(v).trim())) {
        return `${p.label}: required`;
      }
      if (p.type === 'radio' && !v) {
        return `${p.label}: please choose an option`;
      }
    }
    return null;
  };

  const handleNext = () => {
    const err = validateSection(currentSection);
    if (err) { toast.error(err); return; }
    setStepIdx((i) => Math.min(i + 1, sections.length - 1));
  };

  const buildSubmitBody = () => {
    // Convert all photo arrays from [{s3Key, thumbUrl}] to [s3Key] for backend.
    const stateForSubmit = {};
    for (const k of Object.keys(state)) {
      const v = state[k];
      if (Array.isArray(v) && v.length > 0 && v[0] && typeof v[0] === 'object' && 's3Key' in v[0]) {
        stateForSubmit[k] = v.map((it) => it.s3Key).filter(Boolean);
      } else {
        stateForSubmit[k] = v;
      }
    }
    const allExteriorKey = phase === PHASES.PREHIRE ? 'exterior_photos' : 'return_exterior_photos';
    const exteriorPhotos = stateForSubmit[allExteriorKey] || [];
    // PHASE 1 (P0): use the actual template_type from the resolved template
    // (e.g. `motorcycle_default`, `trailer_default`, `boat_default`, …).
    // Falls back to specialty_asset to preserve historical behaviour for
    // bookings whose template_type isn't otherwise resolvable.
    const tt = template?.template_type || TEMPLATE_TYPES.SPECIALTY_ASSET;
    const base = {
      template_type: tt,
      // Preserve the existing payload field name so backend storage and
      // any downstream consumers do not change.
      specialty_data: { ...stateForSubmit, template_version: 1, category: vehicleCategory },
    };
    if (phase === PHASES.PREHIRE) {
      base.exterior_photos = exteriorPhotos;
    } else {
      base.return_exterior_photos = exteriorPhotos;
    }
    return base;
  };

  const handleSubmit = async () => {
    // validate every section up-front
    for (const s of sections) {
      const err = validateSection(s);
      if (err) { toast.error(err); return; }
    }
    try {
      setSubmitting(true);
      const body = buildSubmitBody();
      const endpoint = phase === PHASES.PREHIRE
        ? `${API}/bookings/${booking.booking_id || booking.id}/prehire/submit`
        : `${API}/bookings/${booking.booking_id || booking.id}/posthire/submit`;
      await axios.post(endpoint, body, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(phase === PHASES.PREHIRE ? 'Pre-hire submitted' : 'Post-hire submitted');
      onSubmitted?.();
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || 'Submission failed';
      toast.error(typeof msg === 'string' ? msg : 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (!currentSection) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid={`specialty-wizard-${phase}`}>
      <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-5 border-b border-slate-200 flex items-start justify-between gap-3 sticky top-0 bg-white z-10">
          <div className="flex-1">
            <p className="text-xs uppercase tracking-wide text-amber-600 font-semibold">{template.title}</p>
            <h2 className="text-lg font-bold text-slate-900 mt-0.5">{currentSection.title}</h2>
            <p className="text-sm text-slate-500 mt-0.5">{currentSection.description}</p>
            <p className="text-xs text-slate-400 mt-2">Step {stepIdx + 1} of {sections.length}</p>
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600" data-testid="specialty-wizard-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {currentSection.prompts.map((p) => {
            const v = getVal(p.key);
            if (p.type === 'photos') {
              return (
                <div key={p.key}>
                  <Label className="text-sm">{p.label}{p.required && ' *'}</Label>
                  <div className="mt-2">
                    <PhotoGrid
                      items={v || []}
                      min={p.min}
                      max={p.max}
                      uploading={uploadingKey === p.key}
                      onAdd={(file) => handlePhotoAdd(p.key, file)}
                      onRemove={(i) => handlePhotoRemove(p.key, i)}
                      testIdPrefix={`photo-${p.key}`}
                    />
                  </div>
                </div>
              );
            }
            if (p.type === 'checkbox') {
              return (
                <label key={p.key} className="flex items-start gap-3 cursor-pointer text-sm">
                  <input
                    type="checkbox"
                    className="mt-0.5 rounded border-slate-300"
                    checked={!!v}
                    onChange={(e) => setVal(p.key, e.target.checked)}
                    data-testid={`check-${p.key}`}
                  />
                  <span className="text-slate-700">{p.label}{p.required && ' *'}</span>
                </label>
              );
            }
            if (p.type === 'text') {
              return (
                <div key={p.key}>
                  <Label className="text-sm">{p.label}{p.required && ' *'}</Label>
                  <Textarea
                    rows={2}
                    placeholder={p.placeholder || ''}
                    value={v || ''}
                    onChange={(e) => setVal(p.key, e.target.value)}
                    className="mt-1 text-sm"
                    data-testid={`text-${p.key}`}
                  />
                </div>
              );
            }
            if (p.type === 'radio') {
              return (
                <div key={p.key}>
                  <Label className="text-sm">{p.label}{p.required && ' *'}</Label>
                  <div className="mt-2 space-y-1.5">
                    {p.options.map((opt) => (
                      <label key={opt.value} className="flex items-center gap-2 text-sm cursor-pointer">
                        <input
                          type="radio"
                          name={p.key}
                          checked={v === opt.value}
                          onChange={() => setVal(p.key, opt.value)}
                          data-testid={`radio-${p.key}-${opt.value}`}
                        />
                        <span>{opt.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              );
            }
            return null;
          })}
        </div>

        <div className="p-5 border-t border-slate-200 flex items-center justify-between sticky bottom-0 bg-white">
          <Button
            variant="ghost"
            onClick={() => setStepIdx((i) => Math.max(i - 1, 0))}
            disabled={stepIdx === 0 || submitting}
            data-testid="specialty-wizard-back"
          >
            <ChevronLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          {stepIdx < sections.length - 1 ? (
            <Button onClick={handleNext} className="bg-[#004B8D] hover:bg-[#003a6f]" data-testid="specialty-wizard-next">
              Next <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button onClick={handleSubmit} disabled={submitting} className="bg-emerald-600 hover:bg-emerald-700" data-testid="specialty-wizard-submit">
              {submitting ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Submitting…</> : <><CheckCircle2 className="w-4 h-4 mr-1" /> Submit</>}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SpecialtyInspectionWizard;
